import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from 'src/app/models/common-models';
import { EnvironmentService } from 'src/app/services/environment.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-location-card',
  templateUrl: './location-card.component.html',
  styleUrls: ['./location-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() location!: Location;
  @Input() active = false;
  @Input() newestLocationId: number | null = null;
  
  currentImageIndex = 0;
  imageLoadError = false;
  slideDirection: 'left' | 'right' | null = null;
  favorited = false; // Track favorite state
  private timeoutIds: number[] = []; // Track setTimeout calls for cleanup

  constructor(
    private cdr: ChangeDetectorRef,
    private environmentService: EnvironmentService,
    private router: Router,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    // Load favorite state from localStorage
    this.loadFavoriteState();
    // Preload images for smoother transitions
    this.preloadImages();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If newestLocationId changes, trigger change detection to update badge
    if (changes['newestLocationId']) {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    // Clean up all pending setTimeout calls
    this.timeoutIds.forEach(id => clearTimeout(id));
    this.timeoutIds = [];
  }

  private loadFavoriteState(): void {
    try {
      const stored = localStorage.getItem('favoriteLocations');
      if (stored) {
        const favorites = new Set(JSON.parse(stored));
        this.favorited = favorites.has(this.location.Id.toString());
        this.cdr.markForCheck();
      }
    } catch (error) {
      // Silently fail if localStorage is unavailable
      console.warn('Failed to load favorite state from localStorage', error);
    }
  }

  private saveFavoriteState(): void {
    try {
      const stored = localStorage.getItem('favoriteLocations');
      const favorites = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
      
      if (this.favorited) {
        favorites.add(this.location.Id.toString());
      } else {
        favorites.delete(this.location.Id.toString());
      }
      
      localStorage.setItem('favoriteLocations', JSON.stringify(Array.from(favorites)));
    } catch (error) {
      // Silently fail if localStorage is unavailable
      console.warn('Failed to save favorite state to localStorage', error);
    }
  }

  get badge(): {label: string, class: string} | null {
    if (!this.location.OpeningDate) return null;
    const openingDate = new Date(this.location.OpeningDate);
    const now = new Date();
    
    // Coming Soon: if OpeningDate is in the future
    if (openingDate > now) {
      return { label: 'COMING SOON', class: 'bg-primary' };
    }
    
    // New: if this is the most recently opened location (first in sorted list by date desc)
    if (this.newestLocationId !== null && this.location.Id === this.newestLocationId) {
      console.log('[LocationCard] Showing NEW badge for location:', {
        locationId: this.location.Id,
        locationName: this.location.Name,
        newestLocationId: this.newestLocationId,
        openingDate: this.location.OpeningDate
      });
      return { label: 'NEW', class: 'bg-primary' };
    }
    
    return null;
  }

  get images(): string[] {
    return this.location.LocationImages?.map(img => img.ImageUrl).filter(url => url) || [];
  }

  get currentImage(): string | null {
    return this.images.length > 0 ? this.images[this.currentImageIndex] : null;
  }
  
  // Preload adjacent images for smoother transitions
  private preloadImages(): void {
    if (this.images.length <= 1) return;
    
    const preloadIndexes = [
      (this.currentImageIndex + 1) % this.images.length,
      (this.currentImageIndex - 1 + this.images.length) % this.images.length
    ];
    
    preloadIndexes.forEach(index => {
      const img = new Image();
      img.src = this.images[index];
    });
  }

  get hasImages(): boolean {
    return this.images.length > 0 && !this.imageLoadError;
  }

  onImageError(): void {
    this.imageLoadError = true;
    this.cdr.markForCheck();
  }

  get imageCount(): number {
    return this.images.length || 5; // Default to 5 dots if no images
  }

  nextImage(event: Event): void {
    event.stopPropagation();
    if (this.images.length > 0) {
      this.slideDirection = 'right';
      this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
      // Preload next images
      this.preloadImages();
      // Force change detection immediately
      this.cdr.detectChanges();
      // Reset direction after animation
      const timeoutId = window.setTimeout(() => {
        this.slideDirection = null;
        this.cdr.detectChanges();
      }, 300);
      this.timeoutIds.push(timeoutId);
    }
  }

  previousImage(event: Event): void {
    event.stopPropagation();
    if (this.images.length > 0) {
      this.slideDirection = 'left';
      this.currentImageIndex = (this.currentImageIndex - 1 + this.images.length) % this.images.length;
      // Preload next images
      this.preloadImages();
      // Force change detection immediately
      this.cdr.detectChanges();
      // Reset direction after animation
      const timeoutId = window.setTimeout(() => {
        this.slideDirection = null;
        this.cdr.detectChanges();
      }, 300);
      this.timeoutIds.push(timeoutId);
    }
  }

  selectImage(index: number): void {
    if (index >= 0 && index < this.images.length) {
      // Determine slide direction
      this.slideDirection = index > this.currentImageIndex ? 'right' : 'left';
      this.currentImageIndex = index;
      // Preload next images
      this.preloadImages();
      // Force change detection immediately
      this.cdr.detectChanges();
      // Reset direction after animation
      const timeoutId = window.setTimeout(() => {
        this.slideDirection = null;
        this.cdr.detectChanges();
      }, 300);
      this.timeoutIds.push(timeoutId);
    }
  }

  isActiveDot(index: number): boolean {
    return index === this.currentImageIndex;
  }

  get canNavigate(): boolean {
    return this.images.length > 1;
  }

  toggleFavorite(event: Event): void {
    event.stopPropagation();
    this.favorited = !this.favorited;
    this.saveFavoriteState(); // Persist to localStorage as fallback
    this.cdr.markForCheck();
    // TODO(INTEG): Persist favorite state to backend/API (replace localStorage)
  }

  /**
   * Convert full state name to abbreviation
   * Returns the abbreviation if found, otherwise returns the original state name
   */
  getStateAbbreviation(state: string): string {
    const stateMap: { [key: string]: string } = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
      'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
      'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
      'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
      'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
      'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
      'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
      'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
      'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
      'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
      'Wisconsin': 'WI', 'Wyoming': 'WY',
      'District of Columbia': 'DC', 'Puerto Rico': 'PR'
    };
    
    // Check if already an abbreviation (2 letters, uppercase)
    if (state.length === 2 && state === state.toUpperCase()) {
      return state;
    }
    
    return stateMap[state] || state;
  }

  /**
   * Format distance for display
   * Returns formatted string like "2.3 miles away" or null if no distance
   */
  getDistanceDisplay(): string | null {
    const distance = (this.location as any).distance;
    if (distance === undefined || distance === null) {
      return null;
    }
    return `${distance.toFixed(1)} miles away`;
  }

  /**
   * Open Google Maps with directions to the location
   */
  getDirections(event: Event): void {
    event.stopPropagation(); // Prevent card click from firing
    const url = `https://www.google.com/maps/dir/?api=1&destination=${this.location.Latitude},${this.location.Longitude}`;
    window.open(url, '_blank');
  }

  /**
   * Get the location select URL from config
   */
  get locationSelectUrl(): string {
    return this.environmentService.getFacilityConfig().locationSelectUrl || '/get-started';
  }

  /**
   * Navigate to location select page after storing location in localStorage
   */
  selectLocation(event: Event): void {
    event.stopPropagation(); // Prevent card click from firing
    // Store location as temp location in localStorage
    this.localStorageService.setTempLocation(this.location);
    // Navigate to the configured URL
    this.router.navigate([this.locationSelectUrl]);
  }
}
